/**
 * useCurrentYearVariantPricingHistory — fetches current-year monthly +
 * quarterly baseline cost / selling price averages for one product from
 * /product-variant-pricing-history/current-year.
 *
 * Independent from useProductVariantPricingHistory — paginating the table
 * below the charts MUST NOT refetch or reshape the aggregates.
 * Same shape as the deceased useCurrentYearPricing (which lived on
 * the retired reports worker before its surface was dropped).
 *
 * Refetches when `productId` changes. Cancels in-flight requests on
 * unmount so a rapid dropdown change can't deliver stale data.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES } from '../constants/routes.constants'
import type {
  MonthlyVariantPricingHistoryBucket,
  QuarterlyVariantPricingHistoryBucket,
  VariantAxis
} from '../models/product-variant-pricing-history.model'
import { parseApiError } from '../utils/api/parseApiError'

export type {
  MonthlyVariantPricingHistoryBucket as MonthlyBucket,
  QuarterlyVariantPricingHistoryBucket as QuarterlyBucket,
  VariantAxis
} from '../models/product-variant-pricing-history.model'

type MonthlyBucket = MonthlyVariantPricingHistoryBucket
type QuarterlyBucket = QuarterlyVariantPricingHistoryBucket

export interface UseCurrentYearVariantPricingHistoryReturn {
  readonly year: number | null
  readonly axes: ReadonlyArray<VariantAxis>
  readonly monthly: ReadonlyArray<MonthlyBucket>
  readonly quarterly: ReadonlyArray<QuarterlyBucket>
  readonly isLoading: boolean
  readonly error: string | null
  readonly reload: () => void
}

interface ApiOkBody {
  readonly success: true
  readonly data: {
    readonly product_id: string
    readonly year: number
    readonly axes: VariantAxis[]
    readonly monthly: MonthlyBucket[]
    readonly quarterly: QuarterlyBucket[]
  }
}

/**
 * @param productId      product whose charts to load (null → cleared)
 * @param axisValueIds   selected axis value ids to narrow the charts to
 *                       (one per axis the operator filtered; [] → whole
 *                       product). Drives the monthly/quarterly aggregates
 *                       ONLY — `axes` (the dropdown options) stays the
 *                       full set for the product regardless of filter.
 */
export function useCurrentYearVariantPricingHistory(
  productId: string | null,
  axisValueIds: ReadonlyArray<string> = []
): UseCurrentYearVariantPricingHistoryReturn {
  const { authedFetch } = useAuth()
  const [year, setYear] = useState<number | null>(null)
  const [axes, setAxes] = useState<VariantAxis[]>([])
  const [monthly, setMonthly] = useState<MonthlyBucket[]>([])
  const [quarterly, setQuarterly] = useState<QuarterlyBucket[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const reloadTickRef = useRef<number>(0)
  const [reloadTick, setReloadTick] = useState<number>(0)
  const lastProductRef = useRef<string | null>(null)

  const reload = useCallback((): void => {
    reloadTickRef.current += 1
    setReloadTick(reloadTickRef.current)
  }, [])

  // Stable dependency for the selected filter (order-independent).
  const filterKey = [...axisValueIds].sort().join(',')

  useEffect(() => {
    if (productId === null || productId.trim() === '') {
      setYear(null)
      setAxes([])
      setMonthly([])
      setQuarterly([])
      setIsLoading(false)
      setError(null)
      lastProductRef.current = null
      return
    }
    // New product → drop the previous product's axis options so stale
    // dropdowns don't show during the load. A filter-only change keeps them.
    if (lastProductRef.current !== productId) {
      setAxes([])
      lastProductRef.current = productId
    }
    const ac = new AbortController()
    setIsLoading(true)
    setError(null)
    ;(async () => {
      try {
        const params = new URLSearchParams({ productId })
        for (const id of filterKey === '' ? [] : filterKey.split(',')) {
          params.append('axisValue', id)
        }
        const url = `${APIROUTES.PRODUCTVARIANTPRICINGHISTORYCURRENTYEAR}?${params.toString()}`
        const res = await authedFetch(url, { signal: ac.signal })
        if (!res.ok) {
          setError((await parseApiError(res)).message)
          setMonthly([])
          setQuarterly([])
          setYear(null)
          return
        }
        const body = (await res.json()) as ApiOkBody
        setAxes(body.data.axes)
        setMonthly(body.data.monthly)
        setQuarterly(body.data.quarterly)
        setYear(body.data.year)
      } catch (err) {
        if ((err as { name?: string }).name === 'AbortError') return
        setError(err instanceof Error ? err.message : String(err))
      } finally {
        if (!ac.signal.aborted) setIsLoading(false)
      }
    })()
    return (): void => ac.abort()
  }, [authedFetch, productId, filterKey, reloadTick])

  return { year, axes, monthly, quarterly, isLoading, error, reload }
}
